import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import { Modal, Checkbox, TextAreaInput, Button } from "@toddle-edu/ds-web";
import { useI18n } from "Hooks";
import classes from "./EventUpdateConfirmationModal.scss";
import {
  mapStateToProps,
  mapActionCreators,
  setValue,
} from "../../modules/Utils";
import { connect } from "react-redux";

const EventUpdateConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  title = "calendar:update_event",
  confirmationMessage = "calendar:update_event_confirmation_message",
  checkboxLabel = "calendar:notify_all_the_participants_about_the_update_on_the_event",
  messageLabel = "calendar:message",
  messagePlaceholder = "calendar:add_your_message_here",
  warningMessage = "calendar:participants_notification_warning",
  saveButtonLabel = "common:confirm",
  cancelButtonLabel = "common:cancel",
  showCheckbox = true,
  showMessageInput = true,
  showWarning = true,
  maxMessageLength = 300,
  testId = "event-update-confirmation-modal",
  height = "472px",
  showIcon = false,
  updateEventModalV2,
  showMessageLabel = true,
}) => {
  const { t } = useI18n(["calendar", "common"]);
  const [sendMessage, setSendMessage] = useState(false);
  const [message, setMessage] = useState("");
  const toggleNotifyParticipants = setValue(
    "shouldNotifyParticipants",
    updateEventModalV2
  );

  const handleClose = useCallback(() => {
    setSendMessage(true);
    setMessage("");
    onClose();
  }, [onClose]);

  const handleConfirm = useCallback(() => {
    onConfirm({
      sendMessage,
      message: sendMessage ? message : "",
    });
    handleClose();
  }, [onConfirm, sendMessage, message, handleClose]);

  const handleCheckboxChange = useCallback(checked => {
    setSendMessage(checked);
    toggleNotifyParticipants(checked);
    if (!checked) {
      setMessage("");
    }
  }, []);

  const handleMessageChange = useCallback(
    event => {
      const value = event.target.value;
      if (value.length <= maxMessageLength) {
        setMessage(value);
      }
    },
    [maxMessageLength]
  );

  const messageCharacterCount = `${message.length}/${maxMessageLength}`;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      width="480px"
      height={height}
      testId={testId}
    >
      <Modal.Header
        title={t(title)}
        isClosable={true}
        testId={`${testId}-header`}
      />

      <Modal.Body testId={`${testId}-body`}>
        <div className={classes.modalContent}>
          {/* Confirmation Message */}
          <div className={classes.confirmationText}>
            {t(confirmationMessage)}
          </div>

          {/* Checkbox for sending message */}
          {showCheckbox && (
            <div className={classes.checkboxContainer}>
              <Checkbox
                isChecked={sendMessage}
                onChange={e => handleCheckboxChange(!sendMessage)}
                size="medium"
                testId={`${testId}-send-message-checkbox`}
              >
                <span>{t(checkboxLabel)}</span>
              </Checkbox>
            </div>
          )}

          {/* Message Input */}
          {showCheckbox && showMessageInput && sendMessage && (
            <div className={classes.messageInputContainer}>
              {showMessageLabel ? (
                <div className={"text-label"}>{t(messageLabel)}</div>
              ) : null}
              <div className={classes.textAreaWrapper}>
                <TextAreaInput
                  value={message}
                  onChange={handleMessageChange}
                  placeholder={t(messagePlaceholder)}
                  minRows={3}
                  maxRows={5}
                  type="outlined-subtle"
                  testId={`${testId}-message-input`}
                  className={classes.messageTextArea}
                />
                <div className={classes.characterCount}>
                  {messageCharacterCount}
                </div>
              </div>
            </div>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer testId={`${testId}-footer`}>
        <div className={classes.footerActions}>
          <Button
            variant="neutral"
            type="outlined"
            onClick={handleClose}
            testId={`${testId}-cancel-button`}
          >
            {t(cancelButtonLabel)}
          </Button>
          <Button
            variant="primary"
            type="fill"
            onClick={handleConfirm}
            testId={`${testId}-save-button`}
          >
            {t(saveButtonLabel)}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

EventUpdateConfirmationModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  title: PropTypes.string,
  confirmationMessage: PropTypes.string,
  checkboxLabel: PropTypes.string,
  messageLabel: PropTypes.string,
  messagePlaceholder: PropTypes.string,
  warningMessage: PropTypes.string,
  saveButtonLabel: PropTypes.string,
  cancelButtonLabel: PropTypes.string,
  showCheckbox: PropTypes.bool,
  showMessageInput: PropTypes.bool,
  showWarning: PropTypes.bool,
  maxMessageLength: PropTypes.number,
  testId: PropTypes.string,
  showMessageLabel: PropTypes.bool,
};

export default connect(
  mapStateToProps,
  mapActionCreators
)(EventUpdateConfirmationModal);
